import { z } from 'zod'
import type { OwnershipGraph } from './entity'
import { fxStanding, observedOnOf } from './fx'
import type { Minor, Quantity } from './money'
import { BASIS_POINTS_FULL, crossRate, invertRate, sumMinor, withoutBasisPoints } from './money'
import type { ValuationKind } from './private-holding'
import { unitLabelOf } from './private-holding'
import type {
  AccountValue,
  EntityValue,
  ExtractionTaxFn,
  InstrumentPrice,
  RollupAccount,
  RollupPosition,
} from './rollup'
import { rollup } from './rollup'

export type InstrumentKind = 'cash' | 'equity' | 'etf' | 'fund' | 'crypto' | 'private'

export type SourceStatus = 'ok' | 'error' | 'not_configured' | 'carried_forward'

/**
 * The name pricing appears under wherever a run is broken down by source, even
 * though it collects nothing of anybody's. Every collector gets a row from
 * `runSnapshot` before pricing even starts; without one of its own, an ECB or
 * CoinGecko failure that priced nothing had nowhere durable to land — only a
 * return value every caller between the job and a page narrowed into a count or
 * a boolean. Declared here, in the domain layer both the job and the page
 * import, rather than duplicated as a bare string on each side of that gap.
 */
export const PRICES_SOURCE_ID = 'prices'

export type Confidence = 'low' | 'medium' | 'high'

export class ValuationError extends Error {}

export interface InstrumentRow {
  id: number
  kind: InstrumentKind
  symbol: string
  name: string
  currency: string
  /** What one unit is called where the household said; null where it did not. */
  unitLabel?: string | null
}

export interface PriceRow {
  instrumentId: number
  date: string
  priceMinor: Minor
  currency: string
  /** Came from the shared feed with none of the household's own sources behind it. */
  fromSharedFeed?: boolean | null
}

export interface FxRow {
  date: string
  base: string
  quote: string
  rate: Quantity
  /** The publication day behind the rate; null on rows written before it was kept. */
  observedOn?: string | null
}

export interface ValuationEventRow {
  instrumentId: number
  date: string
  sharesHeld: Quantity
  pricePerShareMinor: Minor
  /**
   * What the price is counted in. Null on rows written before the event
   * recorded it, which are read off the instrument instead.
   */
  currency?: string | null
  haircutBp: number
  kind: ValuationKind
  round: string | null
  confidence: Confidence
  source: string
}

export const RETIRED_ACCOUNTS_SETTING_KEY = 'retiredAccounts'

export const accountRetirementSchema = z.object({
  accountId: z.number().int().positive(),
  retiredOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD'),
  /**
   * Set only by a machine-written retirement — today, an account that lost its
   * connection row when singleton credentials became connections and has not
   * been reconnected. Absent for every retirement a person chose.
   *
   * The distinction is what stops un-retiring from being a guess: an account a
   * household retired on purpose looks identical to one this reason marks,
   * except for this field, and reading a bare `connectionId IS NULL` as "we did
   * this" would resurrect a holding somebody removed for their own reasons the
   * moment its stable key happened to reappear.
   */
  reason: z.literal('orphaned-connection').optional(),
})

export interface AccountRetirement {
  accountId: number
  /** First date the account stops contributing; every date before it keeps its value. */
  retiredOn: string
  /** See `accountRetirementSchema`; never set by a person. */
  reason?: 'orphaned-connection'
}

/** A malformed setting must not take the whole valuation down with it. */
export function parseRetirements(value: unknown): AccountRetirement[] {
  const parsed = z.array(accountRetirementSchema).safeParse(value ?? [])
  return parsed.success ? parsed.data : []
}

export interface PositionRow {
  accountId: number
  instrumentId: number
  quantity: Quantity
  asOf: string
  confirmed: boolean
  costBasisMinor: Minor | null
  costBasisCurrency: string | null
  /**
   * False on a row no collection wrote: a day the household stated, or one
   * rebuilt from history. Absent counts as collected, the side that flags a
   * day rather than the side that clears one.
   */
  collected?: boolean
}

export interface SourceState {
  source: string
  status: SourceStatus
  staleSince: string | null
  error: string | null
}

export interface PricePoint {
  priceMinor: Minor
  currency: string
  date: string
  fromSharedFeed?: boolean
}

export interface PriceIndex {
  on(instrumentId: number, date: string): PricePoint | undefined
}

export interface FxPoint {
  rate: Quantity
  date: string
  /**
   * The day the rate was published. A figure converted at a rate observed
   * before the day it values is a figure resting on a rate nobody confirmed for
   * that day, and only this tells the two apart.
   */
  observedOn: string
}

export interface FxIndex {
  on(from: string, to: string, date: string): FxPoint | undefined
}

export interface PrivateValuationIndex {
  on(instrumentId: number, date: string): ValuationEventRow | undefined
}

/** That a source reached this account on this date, whatever it found there. */
export interface AccountReportRow {
  accountId: number
  asOf: string
}

export interface PositionsOnDate {
  positions: PositionRow[]
  /**
   * The oldest `as_of` behind the set, whoever wrote it — not how fresh the
   * day is, which `freshAsOf` reads off the collected rows alone.
   */
  oldestAsOf?: string
  /**
   * Whether any account had been heard from by this date. A portfolio that has
   * been sold down to nothing is worth zero; one nobody has collected yet is not
   * worth anything at all, and the two must not render the same.
   */
  reported?: boolean
  /**
   * Which accounts had been heard from, not merely that one had. An account that
   * reported itself empty belongs here as much as one holding millions: both are
   * known, and what is missing from this list is what the day cannot speak for.
   */
  reportingAccountIds?: readonly number[]
}

export interface PositionTimeline {
  on(date: string): PositionsOnDate
  firstDate?: string
  lastDate?: string
}

export interface PrivateValuationDetail {
  instrumentId: number
  symbol: string
  name: string
  /** What the household counts this holding in, so nothing has to assume shares. */
  unitLabel: string
  kind: ValuationKind
  valuationDate: string
  ageDays: number
  pricePerShareMinor: Minor
  sharesHeld: Quantity
  haircutBp: number
  extraHaircutBp: number
  round: string | null
  confidence: Confidence
  source: string
}

/**
 * How much of the household this day can speak for. A source contributes
 * nothing before the day it was connected, and nothing is indistinguishable
 * from zero once it has been summed — so the count of sources behind a figure
 * travels with the figure, and two days built from different sources are never
 * silently compared.
 *
 * Distinct from staleness: a source that has stopped answering still carries
 * its last known positions forward and still counts here, because its money is
 * still in the figure. This says which sources had any history at all.
 */
export interface CoverageFlags {
  /** The sources with data behind this day, sorted. */
  sources: string[]
  /** Every source the household has, sorted — what `sources` is measured against. */
  knownSources: string[]
  /**
   * Every known source has data behind this day or held nothing on it, so no
   * source is missing from the figure. A source in `partial` still counts: this
   * speaks for sources, and `partial` for the accounts inside them.
   */
  complete: boolean
  /**
   * Sources with no data behind this day whose every account is known to have
   * held nothing on it, sorted. Kept apart from `sources`, which stays the
   * sources heard from: a day before an account existed is missing nothing
   * from the figure, yet nobody reported it either.
   */
  empty?: string[]
  /**
   * Sources in `sources` that some counted account has not spoken for yet,
   * sorted by source. One account's history never vouches for a sibling's, or
   * the sibling's value arrives as growth on the day its rows begin.
   */
  partial?: PartialCoverage[]
}

/** A source the day has data from, though not from every account it counts. */
export interface PartialCoverage {
  source: string
  /** Its accounts with no data by this day and no word that they held nothing. */
  missing: number
  /** Its accounts the day counts, so the shortfall can be put as one of two. */
  of: number
}

export interface ValuationFlags {
  stale: boolean
  projected: boolean
  /** False where a company was left untaxed for want of rates, not because it owes nothing. */
  taxRatesConfigured: boolean
  /** The day the oldest input stopped being real. */
  staleSince?: string
  oldestPositionDate?: string
  oldestPriceDate?: string
  unpricedInstrumentIds: number[]
  missingFx: string[]
  /**
   * Pairs the figure was converted at a rate the feed had not confirmed for the
   * date. The amounts are real; the rate behind them is the last one published.
   */
  carriedFx: string[]
  staleSources: SourceState[]
  coverage: CoverageFlags
}

export interface ValuationPoint {
  date: string
  baseCurrency: string
  lookThroughMinor: Minor
  spendableMinor: Minor
  liquidMinor: Minor
  taxMinor: Minor
  projectedMinor: Minor
  /**
   * The part of `projectedMinor` held in accounts whose source was carried
   * forward on the day. A source that stops answering repeats its last
   * positions unconfirmed, so without this one stalled source reaches a reader
   * as two facts: value nobody confirmed, and positions nobody refreshed.
   * Whatever that source was already modelling is in here too, because its
   * rows do not say which were repeated and which were projected.
   */
  carriedMinor: Minor
  personalMinor: Minor
  companyMinor: Minor
  holdsThroughCompany: boolean
  /** See `RollupResult.holdsUnsellable`: where false, Liquid repeats a figure already shown. */
  holdsUnsellable: boolean
  flags: ValuationFlags
}

export interface ValuationSnapshot extends ValuationPoint {
  byAccount: AccountValue[]
  byEntity: EntityValue[]
  privateValuations: PrivateValuationDetail[]
  sources: SourceState[]
  /** False before any source had reported at all — distinct from a net worth of zero. */
  hasPositions: boolean
}

export interface ValuationContext {
  baseCurrency: string
  subjectKey: string
  graph: OwnershipGraph
  accounts: readonly RollupAccount[]
  instruments: ReadonlyMap<number, InstrumentRow>
  prices: PriceIndex
  fx: FxIndex
  privateValuations: PrivateValuationIndex
  positions: PositionTimeline
  /** Discount applied on top of each valuation event's own haircut. */
  haircutBp?: number
  excludeAccountIds?: readonly number[]
  extractionTax?: ExtractionTaxFn
  settings?: Readonly<Record<string, unknown>>
}

export function buildPriceIndex(rows: readonly PriceRow[]): PriceIndex {
  const byInstrument = new Map<number, PricePoint[]>()
  for (const row of rows) {
    const list = byInstrument.get(row.instrumentId) ?? []
    list.push({
      priceMinor: row.priceMinor,
      currency: row.currency,
      date: row.date,
      ...(row.fromSharedFeed === true ? { fromSharedFeed: true } : {}),
    })
    byInstrument.set(row.instrumentId, list)
  }
  for (const list of byInstrument.values()) list.sort(byDate)

  return {
    on: (instrumentId, date) => latestAtOrBefore(byInstrument.get(instrumentId) ?? [], date),
  }
}

/**
 * The feed publishes euro pairs on business days only, so a rate is taken from
 * the latest quote on or before the date and, where the pair was never published
 * at all, derived from the ones that were.
 */
export function buildFxIndex(rows: readonly FxRow[]): FxIndex {
  const pairs = new Map<string, FxPoint[]>()
  const quotesOf = new Map<string, Set<string>>()
  const basesOf = new Map<string, Set<string>>()

  for (const row of rows) {
    const key = pairKey(row.base, row.quote)
    const list = pairs.get(key) ?? []
    list.push({ rate: row.rate, date: row.date, observedOn: observedOnOf(row) })
    pairs.set(key, list)
    addTo(quotesOf, row.base, row.quote)
    addTo(basesOf, row.quote, row.base)
  }
  for (const list of pairs.values()) list.sort(byDate)

  /**
   * A rate stops being an answer about a date once it has stood unconfirmed for
   * long enough: the longer it stands, the more it is a guess about a market
   * that has been moving. Refused here rather than answered with a caveat, so
   * the date reads as one the conversion could not be done on — which the
   * product already reports without counting the holding as worth nothing.
   */
  const direct = (from: string, to: string, date: string): FxPoint | undefined => {
    const point = latestAtOrBefore(pairs.get(pairKey(from, to)) ?? [], date)
    if (point === undefined) return undefined
    return fxStanding(point.observedOn, date) === 'expired' ? undefined : point
  }

  return {
    on(from, to, date) {
      if (from === to) return { rate: '1', date, observedOn: date }

      const straight = direct(from, to, date)
      if (straight) return straight

      const inverse = direct(to, from, date)
      if (inverse) {
        return { rate: invertRate(inverse.rate), date: inverse.date, observedOn: inverse.observedOn }
      }

      // A cross is only as fresh as its older leg, which is why the two dates
      // are carried through rather than the date the rate was asked for.
      for (const quote of quotesOf.get(from) ?? []) {
        if (!quotesOf.get(to)?.has(quote)) continue
        const a = direct(from, quote, date)
        const b = direct(to, quote, date)
        if (a && b) {
          return {
            rate: crossRate(b.rate, a.rate),
            date: olderOf(a.date, b.date),
            observedOn: olderOf(a.observedOn, b.observedOn),
          }
        }
      }

      for (const base of basesOf.get(from) ?? []) {
        if (!basesOf.get(to)?.has(base)) continue
        const a = direct(base, from, date)
        const b = direct(base, to, date)
        if (a && b) {
          return {
            rate: crossRate(a.rate, b.rate),
            date: olderOf(a.date, b.date),
            observedOn: olderOf(a.observedOn, b.observedOn),
          }
        }
      }

      return undefined
    },
  }
}

/**
 * What a valuation event's price is counted in.
 *
 * The instrument's currency is the fallback and not the answer: it says what
 * the holding is quoted in now, and whoever reports the instrument next may
 * change it. An event that predates such a change read through it is an old
 * figure converted at a currency it was never priced in — so only a row from
 * before events carried their own falls back, and it falls back to the reading
 * it has always been given.
 */
export function valuationEventCurrency<T extends string | undefined>(
  event: Pick<ValuationEventRow, 'currency'>,
  instrumentCurrency: T,
): string | T {
  return event.currency ?? instrumentCurrency
}

/**
 * A round that has already happened stays the price for every date after it and
 * for no date before it, so a new round never reprices last year's history.
 */
export function buildPrivateValuationIndex(
  rows: readonly ValuationEventRow[],
): PrivateValuationIndex {
  const byInstrument = new Map<number, ValuationEventRow[]>()
  for (const row of rows) {
    const list = byInstrument.get(row.instrumentId) ?? []
    list.push(row)
    byInstrument.set(row.instrumentId, list)
  }
  for (const list of byInstrument.values()) list.sort(byDate)

  return {
    on: (instrumentId, date) => latestAtOrBefore(byInstrument.get(instrumentId) ?? [], date),
  }
}

/**
 * Positions are resolved a whole account at a time, from the last date the
 * account was heard from on or before the one being asked about: a source
 * reports everything an account holds at once, so a holding absent from that
 * date was sold rather than left out.
 *
 * A date the account reported on counts even where it left no position behind,
 * which is what stops an account that has been emptied from going on reporting
 * the money it used to hold. An account nobody collects any more never gets that
 * last word, so retiring it stays the only way to end its carry-forward; it
 * applies from the retirement date on and leaves every earlier date untouched.
 */
export function buildPositionTimeline(
  rows: readonly PositionRow[],
  retirements: readonly AccountRetirement[] = [],
  reports: readonly AccountReportRow[] = [],
): PositionTimeline {
  const byAccount = new Map<number, Map<string, PositionRow[]>>()
  for (const row of rows) {
    const dates = byAccount.get(row.accountId) ?? new Map<string, PositionRow[]>()
    const list = dates.get(row.asOf) ?? []
    list.push(row)
    dates.set(row.asOf, list)
    byAccount.set(row.accountId, dates)
  }

  const heardOn = new Map<number, Set<string>>()
  for (const [accountId, dates] of byAccount) heardOn.set(accountId, new Set(dates.keys()))
  for (const report of reports) {
    const dates = heardOn.get(report.accountId) ?? new Set<string>()
    dates.add(report.asOf)
    heardOn.set(report.accountId, dates)
  }

  const datesOf = new Map<number, string[]>()
  for (const [accountId, dates] of heardOn) datesOf.set(accountId, [...dates].sort())

  const all = [...new Set([...rows.map((r) => r.asOf), ...reports.map((r) => r.asOf)])].sort()
  const retiredOn = earliestRetirements(retirements)

  const timeline: PositionTimeline = {
    on(date) {
      const positions: PositionRow[] = []
      const reportingAccountIds: number[] = []
      let oldestAsOf: string | undefined

      for (const [accountId, dates] of datesOf) {
        const retired = retiredOn.get(accountId)
        if (retired !== undefined && retired <= date) continue

        const asOf = latestStringAtOrBefore(dates, date)
        if (asOf === undefined) continue
        reportingAccountIds.push(accountId)

        const held = byAccount.get(accountId)?.get(asOf) ?? []
        // An account holding nothing has nothing that can go stale either, so it
        // drops out rather than dating the whole day to its last report.
        if (held.length === 0) continue

        positions.push(...held)
        if (oldestAsOf === undefined || asOf < oldestAsOf) oldestAsOf = asOf
      }

      const reported = reportingAccountIds.length > 0
      return oldestAsOf === undefined
        ? { positions, reported, reportingAccountIds }
        : { positions, oldestAsOf, reported, reportingAccountIds }
    },
  }

  if (all.length > 0) {
    timeline.firstDate = all[0]
    timeline.lastDate = all[all.length - 1]
  }
  return timeline
}

export function valueOn(
  ctx: ValuationContext,
  date: string,
  sources: readonly SourceState[] = [],
): ValuationSnapshot {
  const excluded = new Set(ctx.excludeAccountIds ?? [])
  const retired = retiredOn(parseRetirements(ctx.settings?.[RETIRED_ACCOUNTS_SETTING_KEY]), date)
  const resolved = withoutRetired(ctx.positions.on(date), retired)
  const held = resolved.positions.filter((p) => !excluded.has(p.accountId))
  const extraHaircutBp = ctx.haircutBp ?? 0

  const prices = new Map<number, InstrumentPrice>()
  const privateValuations: PrivateValuationDetail[] = []
  let oldestPriceDate: string | undefined

  const positions: RollupPosition[] = held.map((row) => {
    const instrument = ctx.instruments.get(row.instrumentId)
    if (!instrument) throw new ValuationError(`no instrument ${row.instrumentId}`)

    if (instrument.kind === 'private') {
      const event = ctx.privateValuations.on(row.instrumentId, date)
      if (!event) return toRollupPosition(row, row.quantity)

      if (!prices.has(row.instrumentId)) {
        prices.set(row.instrumentId, {
          priceMinor: event.pricePerShareMinor,
          currency: valuationEventCurrency(event, instrument.currency),
        })
        privateValuations.push({
          instrumentId: instrument.id,
          symbol: instrument.symbol,
          name: instrument.name,
          unitLabel: unitLabelOf(instrument.unitLabel),
          kind: event.kind,
          valuationDate: event.date,
          ageDays: daysBetween(event.date, date),
          pricePerShareMinor: event.pricePerShareMinor,
          sharesHeld: event.sharesHeld,
          haircutBp: event.haircutBp,
          extraHaircutBp,
          round: event.round,
          confidence: event.confidence,
          source: event.source,
        })
      }

      return toRollupPosition(row, haircutQuantity(row.quantity, event.haircutBp, extraHaircutBp))
    }

    const price = ctx.prices.on(row.instrumentId, date)
    if (price) {
      prices.set(row.instrumentId, { priceMinor: price.priceMinor, currency: price.currency })
      if (oldestPriceDate === undefined || price.date < oldestPriceDate) oldestPriceDate = price.date
    }
    return toRollupPosition(row, row.quantity)
  })

  const counted = ctx.accounts.filter((a) => !excluded.has(a.id) && !retired.has(a.id))

  const result = rollup({
    date,
    baseCurrency: ctx.baseCurrency,
    subjectKey: ctx.subjectKey,
    graph: ctx.graph,
    accounts: counted,
    positions,
    priceOf: (instrumentId) => prices.get(instrumentId),
    fxRate: (from, to, on) => ctx.fx.on(from, to, on),
    instrumentKindOf: (instrumentId) => ctx.instruments.get(instrumentId)?.kind,
    ...(ctx.extractionTax ? { extractionTax: ctx.extractionTax } : {}),
    ...(ctx.settings ? { settings: ctx.settings } : {}),
  })

  // A verdict on a source the figure counts no account of is about money that
  // is not in it — a private holding the toggle left out, a retired account.
  const countedSources = new Set(counted.map((a) => a.source))
  const staleSources = sources.filter((s) => s.status === 'carried_forward' && countedSources.has(s.source))
  const fresh = freshAsOf(held)
  const positionsAreStale = fresh !== undefined && fresh < date

  const flags: ValuationFlags = {
    // A rate the feed did not confirm for this date is the same kind of claim as
    // a position no source reported today, so it reaches the reader the way that
    // one already does rather than through a second vocabulary of its own.
    stale: positionsAreStale || staleSources.length > 0 || result.carriedFx.length > 0,
    projected: held.some((p) => !p.confirmed),
    taxRatesConfigured: result.taxRatesConfigured,
    unpricedInstrumentIds: result.missingPrices,
    missingFx: result.missingFx,
    carriedFx: result.carriedFx,
    staleSources,
    coverage: coverageOf(counted, resolved.reportingAccountIds ?? []),
  }

  const staleSince = earliest([
    ...staleSources.map((s) => s.staleSince),
    positionsAreStale ? fresh : undefined,
    result.carriedFxSince,
  ])
  if (staleSince !== undefined) flags.staleSince = staleSince
  if (resolved.oldestAsOf !== undefined) flags.oldestPositionDate = resolved.oldestAsOf
  if (oldestPriceDate !== undefined) flags.oldestPriceDate = oldestPriceDate

  // Summed from the same attributed account values as `projectedMinor`, so the
  // two subtract to the øre. Taken by account rather than by row: a stalled
  // source's projected rows are stored exactly like its repeated ones, so what
  // is left is the value unconfirmed outside the sources that stalled.
  const carried = new Set(staleSources.map((s) => s.source))
  const carriedMinor = sumMinor(
    result.byAccount.filter((a) => carried.has(a.source)).map((a) => a.projectedMinor),
  )

  return {
    date,
    baseCurrency: result.baseCurrency,
    lookThroughMinor: result.lookThroughMinor,
    spendableMinor: result.spendableMinor,
    liquidMinor: result.liquidMinor,
    taxMinor: result.taxMinor,
    projectedMinor: result.projectedMinor,
    carriedMinor,
    personalMinor: result.personalMinor,
    companyMinor: result.companyMinor,
    holdsThroughCompany: result.holdsThroughCompany,
    holdsUnsellable: result.holdsUnsellable,
    flags,
    byAccount: result.byAccount,
    byEntity: result.byEntity,
    privateValuations,
    sources: [...sources],
    hasPositions: held.length > 0 || resolved.reported === true,
  }
}

export function valueSeries(
  ctx: ValuationContext,
  dates: readonly string[],
  sourcesOn: (date: string) => readonly SourceState[] = () => [],
): ValuationPoint[] {
  return dates.map((date) => toPoint(valueOn(ctx, date, sourcesOn(date))))
}

export function toPoint(snapshot: ValuationSnapshot): ValuationPoint {
  const { byAccount, byEntity, privateValuations, sources, hasPositions, ...point } = snapshot
  return point
}

function haircutQuantity(quantity: Quantity, haircutBp: number, extraBp: number): Quantity {
  return withoutBasisPoints(
    withoutBasisPoints(quantity, assertHaircut(haircutBp)),
    assertHaircut(extraBp),
  )
}

function assertHaircut(bp: number): number {
  if (!Number.isInteger(bp) || bp < 0 || bp > BASIS_POINTS_FULL) {
    throw new ValuationError(`haircut out of range: ${bp}`)
  }
  return bp
}

function earliestRetirements(
  retirements: readonly AccountRetirement[],
): ReadonlyMap<number, string> {
  const dates = new Map<number, string>()
  for (const retirement of retirements) {
    const current = dates.get(retirement.accountId)
    if (current === undefined || retirement.retiredOn < current) {
      dates.set(retirement.accountId, retirement.retiredOn)
    }
  }
  return dates
}

function retiredOn(
  retirements: readonly AccountRetirement[],
  date: string,
): ReadonlySet<number> {
  const ids = new Set<number>()
  for (const [accountId, retired] of earliestRetirements(retirements)) {
    if (retired <= date) ids.add(accountId)
  }
  return ids
}

/**
 * Retirements are configuration, so a timeline is often built before they are
 * known; dropping them again here is what makes the setting reach the figures.
 * The oldest row is recomputed too, because a retired account is no longer
 * behind the figure.
 */
function withoutRetired(resolved: PositionsOnDate, retired: ReadonlySet<number>): PositionsOnDate {
  if (retired.size === 0) return resolved

  const positions = resolved.positions.filter((p) => !retired.has(p.accountId))
  if (positions.length === resolved.positions.length) return resolved

  // A retired account is left in here for `valueOn` to drop from both sides of
  // the count at once; removing it only from this side would leave the day
  // short of a source it is not being asked about.
  const reportingAccountIds = resolved.reportingAccountIds ?? []
  const reported = resolved.reported ?? false
  const oldestAsOf = earliest(positions.map((p) => p.asOf))
  return oldestAsOf === undefined
    ? { positions, reported, reportingAccountIds }
    : { positions, oldestAsOf, reported, reportingAccountIds }
}

/**
 * The oldest day a collection last spoke for the holdings behind the figure,
 * which is as fresh as the figure is.
 *
 * A row no collection wrote is left out. A figure the household stated stands
 * until they change it — its age is the round's, shown beside it (SPEC,
 * private holdings) — and a day rebuilt from history is what the account held.
 * Neither is a source that stopped answering, which is all a stale day claims,
 * and neither is ever dated after the account's latest collection, so the
 * days after that are still dated by it.
 */
function freshAsOf(held: readonly PositionRow[]): string | undefined {
  return earliest(held.filter((p) => p.collected !== false).map((p) => p.asOf))
}

/**
 * A source counts as covering the day once any of its accounts had been heard
 * from, even where that account reported itself empty: an emptied account is a
 * fact about the money, not a hole in the record. What is missing is the source
 * whose first report is still in the future, and that is the one a reader would
 * otherwise mistake for money that was not there yet.
 */
function coverageOf(
  accounts: readonly RollupAccount[],
  reportingAccountIds: readonly number[],
): CoverageFlags {
  const sourceOf = new Map(accounts.map((a) => [a.id, a.source]))
  const known = new Set(accounts.map((a) => a.source))

  const reporting = new Set<string>()
  for (const id of reportingAccountIds) {
    const source = sourceOf.get(id)
    if (source !== undefined) reporting.add(source)
  }

  return {
    sources: [...reporting].sort(),
    knownSources: [...known].sort(),
    complete: reporting.size === known.size,
  }
}

function toRollupPosition(row: PositionRow, quantity: Quantity): RollupPosition {
  return {
    accountId: row.accountId,
    instrumentId: row.instrumentId,
    quantity,
    confirmed: row.confirmed,
    costBasisMinor: row.costBasisMinor,
    costBasisCurrency: row.costBasisCurrency,
  }
}

function latestAtOrBefore<T extends { date: string }>(
  rows: readonly T[],
  date: string,
): T | undefined {
  let low = 0
  let high = rows.length - 1
  let found: T | undefined
  while (low <= high) {
    const mid = (low + high) >> 1
    const row = rows[mid]
    if (row !== undefined && row.date <= date) {
      found = row
      low = mid + 1
    } else {
      high = mid - 1
    }
  }
  return found
}

function latestStringAtOrBefore(dates: readonly string[], date: string): string | undefined {
  let low = 0
  let high = dates.length - 1
  let found: string | undefined
  while (low <= high) {
    const mid = (low + high) >> 1
    const candidate = dates[mid]
    if (candidate !== undefined && candidate <= date) {
      found = candidate
      low = mid + 1
    } else {
      high = mid - 1
    }
  }
  return found
}

function byDate(a: { date: string }, b: { date: string }): number {
  return a.date.localeCompare(b.date)
}

function pairKey(base: string, quote: string): string {
  return `${base}/${quote}`
}

function addTo(map: Map<string, Set<string>>, key: string, value: string): void {
  const set = map.get(key) ?? new Set<string>()
  set.add(value)
  map.set(key, set)
}

function olderOf(a: string, b: string): string {
  return a < b ? a : b
}

function earliest(dates: readonly (string | null | undefined)[]): string | undefined {
  let found: string | undefined
  for (const date of dates) {
    if (!date) continue
    if (found === undefined || date < found) found = date
  }
  return found
}

const MS_PER_DAY = 86_400_000

function daysBetween(from: string, to: string): number {
  const start = Date.parse(`${from}T00:00:00Z`)
  const end = Date.parse(`${to}T00:00:00Z`)
  if (Number.isNaN(start) || Number.isNaN(end)) throw new ValuationError(`bad date: ${from}..${to}`)
  return Math.round((end - start) / MS_PER_DAY)
}
